import { useState, useCallback } from 'react';
import { api } from "@tripian/core";
import Model from "@tripian/model";

interface SearchPoiOptions {
    cityId?: number;
    searchPoiCategories?: number[];
    showOffersOnly?: boolean;
    showBookingsOnly?: boolean;
    page?: number;
    searchText?: string;
    sort?: string;
    rating?: string;
}

interface PaginationInfo {
    total: number;
    perPage: number;
    totalPages: number;
    currentPage: number;
    count: number;
}

const useDiscoverSearchPoi = (cityId?: number) => {
    const [searchResults, setSearchResults] = useState<Model.Poi[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);

    const searchPoi = useCallback(async (options: SearchPoiOptions) => {
        if (!cityId) {
            setError('City ID is required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await api.poisOpenSearchSort({
                cityId,
                search: options.searchText,
                poiCategories: options.searchPoiCategories?.join(','),
                showOffersOnly: options.showOffersOnly,
                showBookingsOnly: options.showBookingsOnly,
                limit: 8,
                page: options.page || 1,
                ...(options.sort && { sort: options.sort }),
                ...(options.rating && { rating: options.rating })
            });

            if (response?.data) {
                // Always replace results for pagination (not load more)
                setSearchResults(response.data);

                // Set pagination info
                if (response.pagination) {
                    setPagination(response.pagination);
                }

                return response.data;
            }
            return [];
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setSearchResults([]);
            setPagination(null);
            return [];
        } finally {
            setLoading(false);
        }
    }, [cityId]);

    return {
        searchResults,
        loading,
        error,
        pagination,
        searchPoi
    };
};

export default useDiscoverSearchPoi;
